<template>
	<div class="relative flex items-center">
		<!-- base copy link button -->
		<BaseTooltip v-if="$store.isUserWorkspaceMember" :enabled="!popoverVisible" placement="bottom">
			<BaseButton
				data-test="button-copy-link"
				class="button-green button-copy-link whitespace-nowrap"
				:class="{'with-dropdown': canUserEditSession && $store.workspace.publicSessionsEnabled}"
				style="min-width: 96px"
				@click="copyLink"
				@mouseenter="copyLinkState = null"
			>
				<template #icon>
					<BaseIcon v-if="copyLinkState === 'success'" name="mono/check-circle" />
					<BaseIcon v-else-if="isPublic" name="mono/view" />
					<BaseIcon v-else name="mono/lock" />
				</template>
				<template v-if="copyLinkState === 'success'">Copied!</template>
				<slot v-else />
			</BaseButton>
			<template v-slot:text>
				<p v-if="isPublic" class="flex space-x-8">
					<BaseIcon name="mono/view" class="mt-4" />
					<span>Anyone can access this link</span>
				</p>
				<p v-else class="flex space-x-8">
					<BaseIcon name="mono/lock" class="mt-2" />
					<span>
						Only {{ $store.collection.accessPermission === 'closed' ? 'folder' : 'workspace' }} members can
						access this link
					</span>
				</p>
			</template>
		</BaseTooltip>
		<BaseButton
			v-else
			:class="$auth.isAuthenticated.value ? 'button-green' : 'link-white mr-8'"
			data-test="button-copy-link-anonymous"
			@click="copyLink"
			@mouseenter="copyLinkState = null"
		>
			<template #icon><BaseIcon name="mono/link" /></template>
			<template v-if="copyLinkState === 'success'">Copied!</template>
			<slot v-else />
		</BaseButton>

		<!-- dropdown permission settings menu -->
		<Popper
			v-if="canUserEditSession && $store.workspace.publicSessionsEnabled"
			v-click-outside="closePopover"
			:isVisible="popoverVisible"
			placement="bottom-end"
			@keyup.escape="closePopover"
		>
			<BaseTooltip :enabled="!popoverVisible" placement="bottom" text="Manage access">
				<button
					class="button-green button-dropdown"
					data-test="copy-link-dropdown-button"
					@click="popoverVisible = !popoverVisible"
				>
					<BaseChevron :flip="popoverVisible" class="text-white" />
				</button>
			</BaseTooltip>
			<template #popup>
				<BasePopover title="Who can view" style="width: 250px" data-test="copy-link-popover">
					<!-- bug-reporters: current session permission -->
					<DropdownOption
						v-if="$store.hasUserBugReporterRole || $workspaceSubscription.isFreePlan"
						:title="
							isPublic
								? 'Anyone with link'
								: `${
										$store.collection.accessPermission === 'closed'
											? 'Folder members'
											: 'Workspace members'
								  }`
						"
						selected
						disabled
					/>

					<!-- admin & members: permission options -->
					<ul v-else class="w-full">
						<li>
							<!-- option 'private'  -->
							<DropdownOption
								:pillText="sessionsPublicByDefault ? null : 'Default'"
								:selected="!isPublic"
								:title="`${
									$store.collection.accessPermission === 'closed'
										? 'Folder members'
										: 'Workspace members'
								}`"
								@select="toggleSessionPermission(false)"
							/>
						</li>
						<li>
							<!-- option 'public'  -->
							<DropdownOption
								:pillText="sessionsPublicByDefault ? 'Default' : null"
								:selected="isPublic"
								title="Anyone with link"
								class="mt-2"
								@select="toggleSessionPermission(true)"
							/>
						</li>
					</ul>
					<!-- set as default checkbox -->
					<template v-if="$store.hasUserBugReporterRole" #footer>
						<p class="px-12 py-4 text-gray-300">
							To change session permissions, please contact a workspace admin to upgrade your role.
						</p>
					</template>
					<template v-else #footer>
						<template v-if="showSetAsDefaultCheckbox">
							<BaseInputGroup
								id="defaultSessionShare"
								horizontal
								labelPosition="end"
								label="Set as default"
								class="popover-link my-4 text-gray-100 items-center"
							>
								<BaseCheckbox
									:modelValue="isDefault"
									color="green"
									class="set-as-default-checkbox mr-2"
									@input="setCurrentPermissionAsDefault"
								/>
							</BaseInputGroup>
							<hr class="border-gray-500 my-0" />
						</template>
						<InviteMemberInPopover location="Copy Link Popover" />
					</template>
				</BasePopover>
			</template>
		</Popper>
	</div>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'

import copyToClipboard from 'shared/utils/copyToClipboard'
import Popper from 'shared/components/Popper.vue'
import analytics from 'shared/utils/analytics'
import createQueryString from 'shared/utils/createQueryString'
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'ButtonCopyLink',
	components: {
		Popper,
		InviteMemberInPopover: defineAsyncComponent(() => import('shared/components/InviteMemberInPopover.vue')),
		DropdownOption: defineAsyncComponent(() => import('shared/components/DropdownOption.vue')),
	},
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession} = storeToRefs(sessionStore)
		return {session, canUserEditSession}
	},
	data() {
		return {
			copyLinkState: null,
			popoverVisible: false,
			wasPublic: this.session.public,
			settingAsDefault: false,
			hasToggledDefaultPermission: false,
		}
	},
	computed: {
		buttonIcon() {
			if (this.copyLinkState === 'success') return 'mono/check-circle'
			return this.isPublic ? 'mono/view' : 'mono/lock'
		},
		tooltipText() {
			return this.isPublic ? 'Only workspace members can open this link' : ''
		},
		sessionsPublicByDefault() {
			if (this.$store.workspaceUserSetting.sessionsPublicByDefault === undefined) {
				return false
			}
			return this.$store.workspaceUserSetting.sessionsPublicByDefault
		},
		isPublic() {
			return this.session.public
		},
		isDefault() {
			if (this.sessionsPublicByDefault) return this.isPublic
			return !this.isPublic
		},
		showSetAsDefaultCheckbox() {
			// don't show the setting for sessions uploaded by SDK, for which is managed in SDK settings
			if (this.session.uploadMethod === 'sdk') return false
			// otherwise always show if current session permission is not the default
			if (this.isPublic !== this.sessionsPublicByDefault) return true
			// whenever the user changes the session's permission...
			if (this.wasPublic !== this.isPublic) {
				// ... always show it if the user has toggled the default at least once
				// to keep it open after checking the checkbox
				return this.hasToggledDefaultPermission
			}
			return false
		},
	},
	watch: {
		popoverVisible() {
			this.wasPublic = this.isPublic
			this.hasToggledDefaultPermission = false
		},
	},
	methods: {
		copyLink() {
			const {params, query} = this.$route
			const queryString = createQueryString(query)
			const url = `${this.$env.VUE_APP_URL}/${params.sessionId}${queryString}`
			copyToClipboard(url)
			this.copyLinkState = 'success'
			const copyLinkAnalytics = this.buildCopyLinkAnalytics(query)
			analytics.track('Session View Link Copied', copyLinkAnalytics)
		},
		buildCopyLinkAnalytics(query) {
			const pane = this.$sessionLayout.devToolsPane
			const {consoleIndex, requestId} = query
			const highlighted = Boolean(pane === 'console' ? consoleIndex : pane === 'network' && requestId)
			const permission = this.isPublic ? 'public' : 'private'
			return {
				cta: 'Shortcut Button',
				pane,
				highlighted,
				devToolsHidden: this.$sessionLayout.devToolsHidden,
				detailsHidden: this.$sessionLayout.detailsHidden,
				permission,
			}
		},
		async toggleSessionPermission(isPublic) {
			if (this.$store.hasUserBugReporterRole) return
			if (this.isPublic === isPublic) return
			// store previous state
			this.wasPublic = this.isPublic

			const updateSessionPermission = (await import('shared/apollo/updateSessionPermission')).default
			await updateSessionPermission({sessionId: this.session.id, isPublic, userId: this.$store.user.id})

			analytics.track('Session Public Status Changed', {
				public: isPublic,
				cta: 'Copy Link Menu Button',
			})
		},
		async setCurrentPermissionAsDefault(checkboxInput) {
			// set switch to define if we keep showing the set as default checkbox
			this.hasToggledDefaultPermission = true

			const sessionsPublicByDefault = this.isPublic ? checkboxInput : !checkboxInput
			const userId = this.$store.user.id
			const {workspaceId} = this.session
			const upsertWorkspaceUserSettings = (await import('shared/apollo/upsertWorkspaceUserSettings')).default
			upsertWorkspaceUserSettings({
				userId,
				workspaceId,
				sessionsPublicByDefault,
			})

			analytics.track('Default Session Permission Changed', {sessionsPublicByDefault})
		},
		closePopover() {
			this.popoverVisible = false
		},
	},
}
</script>

<style scoped>
.button-copy-link {
	justify-content: center;
	min-width: 92px;
	padding: 0;
}

.button-copy-link.with-dropdown {
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
}

.button-copy-link,
.button-copy-link:hover:not([disabled]) {
	filter: none;
}

.button-dropdown {
	width: 28px;
	padding: 0;
	justify-content: center;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	border-left: 1px solid #00945f;
}

.button-dropdown,
.button-dropdown:hover:not([disabled]) {
	filter: none;
}

.set-as-default-checkbox :deep(.el-checkbox__label) {
	font-weight: 500;
	font-size: 13px !important;
}
</style>
